import noUiSlider from "nouislider";

import type { IBulkTableCmd } from "@/data/commands/table/BulkTableCmd";
import { Factory } from "@/data/dao/Factory";
import { RequestCache } from "@/data/dao/RequestCache";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { TableDao } from "@/data/dao/service/TableDao";
import type { IRoom } from "@/data/model/Room";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { UiNotification } from "@/portal/ui/Notification";

export class TableBulkWindow extends BaseModal {
    private static readonly createModifyActionCode: string = "createmodify"
    
    public onExecuted?: { () };
    private $form: JQuery;

    private actionSelect: Select<string>;
    private $formSection: JQuery;

    private roomSelect?: Select<IRoom>;
    private $createFromField?: JQuery;
    private $createToField?: JQuery;
    private $deleteFromField?: JQuery;
    private $deleteToField?: JQuery;
    private $nameConvField?: JQuery;
    private $posIdConvField?: JQuery;
    private seatSlider: any;
    private $seatsField?: JQuery;
    private preferredMinimumOccupancySlider: any;
    private $preferredMinimumOccupancyRow?: JQuery;
    private $preferredMinimumOccupancyField?: JQuery;
    private $submitBtn: JQuery;

    private $preview: JQuery;
    private previewTimeout?: number;
    private previewPromise?: Promise<any>;

    public needsTableReload = false;

    constructor() {
        super(i18n("modal.tableBulk.title"), "modal-lg", false);

        this.$element.addClass("tablebulk");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const actionSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        actionSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.tableBulk.whatToDo") +
                "</h3></div></div>"
        );
        const actionGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.tableBulk.action") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(actionSection)
            .find("> .col-xl-6");
        const s = $(
            '<select name="action" class="form-control"><option value="createmodify" selected>' +
                i18n("modal.tableBulk.editCreateMultipleTables") +
                '</option><option value="delete">' +
                i18n("modal.tableBulk.deleteMultiple") +
                "</option></select>"
        ).appendTo(actionGroup);
        this.actionSelect = Select.createFromSelect(s);
        this.actionSelect.onChange = () => this.changeFormType();

        this.$form.append(
            '<div class="kt-separator kt-separator--border-dashed kt-separator--portlet-fit kt-separator--space-lg"></div>'
        );
        this.$formSection = $('<div class="kt-section kt-section--last"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");

        this.$preview = $('<div class="preview" />').appendTo(this.$footer!);

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-success disabled" disabled="disabled">' +
                i18n("modal.tableBulk.execute") +
                "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.changeFormType();
    }

    protected onShow() {
        this.reset();

        if (this.needsTableReload) {
            this.roomSelect!.fetchData();
            this.needsTableReload = false;
        }
    }

    protected onHide() {}

    private reset() {
        if (this.roomSelect !== undefined) this.roomSelect.setValue();

        if (this.$createFromField !== undefined) this.$createFromField.val("");

        if (this.$createToField !== undefined) this.$createToField.val("");

        if (this.$deleteFromField !== undefined) this.$deleteFromField.val("");

        if (this.$deleteToField !== undefined) this.$deleteToField.val("");

        if (this.$nameConvField !== undefined) this.$nameConvField.val("");

        if (this.$posIdConvField !== undefined) this.$posIdConvField.val("t[Number]");

        if (this.seatSlider !== undefined) this.seatSlider.set(2);
        
        if (this.preferredMinimumOccupancySlider != null) 
            this.preferredMinimumOccupancySlider.set(1);
    }

    private onSubmit() {
        const bulkCommand = this.getCommandDto(true);

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<TableDao>(TableDao)
            .bulk(bulkCommand)
            .then(() => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                this.reset();
                this.close();
                if (this.onExecuted !== undefined) this.onExecuted();
            })
            .catch((ex) => {
                logError(ex);
                const e = ex as DaoError;
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                let exception = i18n("modal.tableBulk.failedEdit");
                if (e?.additionalData?.reason != null) {
                    switch (e.additionalData.reason) {
                        case "no_tables_affected":
                            exception = i18n("modal.tableBulk.failedNoAffected");
                            break;
                        case "table_limit_exceeded":
                            exception = i18n("modal.tableBulk.failedLimitExceeded");
                            break;
                        case "no_active_subscription":
                            exception = i18n("modal.tableBulk.failedNoSubscription");
                            break;
                    }
                }

                UiNotification.showException(exception);
            });
    }

    private getCommandDto(validate: boolean): IBulkTableCmd {
    
        let ac = this.actionSelect.getValue();
        if (ac === undefined)
            ac = TableBulkWindow.createModifyActionCode;

        const seats = ac ===  TableBulkWindow.createModifyActionCode ? parseInt(this.seatSlider.get()) : undefined;
        const bulkCommand: IBulkTableCmd = {
            createFrom:
                ac === TableBulkWindow.createModifyActionCode
                    ? this.$createFromField!.val().length === 0
                        ? undefined
                        : parseInt(this.$createFromField!.val())
                    : undefined,
            createTo:
                ac === TableBulkWindow.createModifyActionCode
                    ? this.$createToField!.val().length === 0
                        ? undefined
                        : parseInt(this.$createToField!.val())
                    : undefined,
            seats: seats,
            preferredMinimumOccupancy: ac === TableBulkWindow.createModifyActionCode && (seats ?? 0) > 0 ? parseInt(this.preferredMinimumOccupancySlider.get()) : undefined,
            removeFrom:
                ac === "delete"
                    ? this.$deleteFromField!.val().length === 0
                        ? undefined
                        : parseInt(this.$deleteFromField!.val())
                    : undefined,
            removeTo:
                ac === "delete"
                    ? this.$deleteToField!.val().length === 0
                        ? undefined
                        : parseInt(this.$deleteToField!.val())
                    : undefined,
            nameConvention:
                ac ===  TableBulkWindow.createModifyActionCode
                    ? this.$nameConvField!.val().length === 0
                        ? undefined
                        : this.$nameConvField!.val()
                    : undefined,
            posIdConvention:
                ac === TableBulkWindow.createModifyActionCode
                    ? this.$posIdConvField!.val().length === 0
                        ? undefined
                        : this.$posIdConvField!.val()
                    : undefined,
            roomId: ac === TableBulkWindow.createModifyActionCode ? this.roomSelect!.getValue() : undefined,
        };

        if (!validate) return bulkCommand;

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (ac ===  TableBulkWindow.createModifyActionCode) {
            if (bulkCommand.roomId === undefined) {
                this.roomSelect!.setError(true);
                hasError = true;
            }

            if (bulkCommand.createFrom === undefined || isNaN(bulkCommand.createFrom)) {
                this.$createFromField!.addClass("is-invalid");
                hasError = true;
            }

            if (bulkCommand.createTo === undefined || isNaN(bulkCommand.createTo)) {
                this.$createToField!.addClass("is-invalid");
                hasError = true;
            }

            if (
                bulkCommand.createTo !== undefined &&
                bulkCommand.createFrom !== undefined &&
                (bulkCommand.createTo < bulkCommand.createFrom || bulkCommand.createFrom <= 0)
            ) {
                this.$createToField!.addClass("is-invalid");
                this.$createFromField!.addClass("is-invalid");
                hasError = true;
            }

            if (
                bulkCommand.createTo !== undefined &&
                bulkCommand.createFrom !== undefined &&
                bulkCommand.createTo - bulkCommand.createFrom > 150
            ) {
                this.$createToField!.addClass("is-invalid");
                this.$createFromField!.addClass("is-invalid");
                hasError = true;
            }

            if (bulkCommand.nameConvention === undefined || bulkCommand.nameConvention.match(/\[Number\]/g) == null) {
                this.$nameConvField!.addClass("is-invalid");
                hasError = true;
            }

            if (bulkCommand.posIdConvention === undefined || bulkCommand.posIdConvention.match(/\[Number\]/g) == null) {
                this.$posIdConvField!.addClass("is-invalid");
                hasError = true;
            }

            if (bulkCommand.seats === undefined || isNaN(bulkCommand.seats)) {
                this.$seatsField!.addClass("is-invalid");
                hasError = true;
            }
        } else if (ac === "delete") {
            if (bulkCommand.removeFrom === undefined || isNaN(bulkCommand.removeFrom)) {
                this.$deleteFromField!.addClass("is-invalid");
                hasError = true;
            }

            if (bulkCommand.removeTo === undefined || isNaN(bulkCommand.removeTo)) {
                this.$deleteToField!.addClass("is-invalid");
                hasError = true;
            }

            if (
                bulkCommand.removeTo !== undefined &&
                bulkCommand.removeFrom !== undefined &&
                (bulkCommand.removeTo < bulkCommand.removeFrom || bulkCommand.removeFrom <= 0)
            ) {
                this.$deleteToField!.addClass("is-invalid");
                this.$deleteFromField!.addClass("is-invalid");
                hasError = true;
            }
        }

        if (hasError) return undefined!;

        return bulkCommand;
    }

    private changeFormType() {
        let ac = this.actionSelect.getValue();
        if (ac === undefined) ac = TableBulkWindow.createModifyActionCode;

        switch (ac) {
            case TableBulkWindow.createModifyActionCode:
                this.showCreateModify();
                break;
            case "delete":
                this.showRemove();
                break;
        }

        this.fetchPreview();
    }

    private showRemove() {
        this.$formSection.empty();
        this.$formSection.append(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.tableBulk.tableNrFromTo") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3"><input type="text" name="removefrom" class="form-control" placeholder="' +
                i18n("modal.tableBulk.tableNrPlaceholder") +
                '" /></div><div class="col-xl-3"><input type="text" name="removeto" class="form-control" placeholder="' +
                i18n("modal.tableBulk.tableNrPlaceholder") +
                '" /></div></div>'
        );

        this.$deleteFromField = this.$formSection.find('input[name="removefrom"]')["TouchSpin"]({
            min: 1,
            max: 100000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.$deleteToField = this.$formSection.find('input[name="removeto"]')["TouchSpin"]({
            min: 1,
            max: 100000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.$deleteFromField!.change(() => {
            const f = this.$deleteFromField!.val().length === 0 ? undefined : parseInt(this.$deleteFromField!.val());
            const t = this.$deleteToField!.val().length === 0 ? undefined : parseInt(this.$deleteToField!.val());

            if ((f !== undefined && f > t!) || t === undefined) this.$deleteToField!.val(f!);

            this.fetchPreview();
        });

        this.$deleteToField!.change(() => {
            const f = this.$deleteFromField!.val().length === 0 ? undefined : parseInt(this.$deleteFromField!.val());
            const t = this.$deleteToField!.val().length === 0 ? undefined : parseInt(this.$deleteToField!.val());

            if ((t !== undefined && t < f!) || f === undefined) this.$deleteFromField!.val(t!);

            this.fetchPreview();
        });

        this.$form.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();

            return false;
        });
    }

    private showCreateModify() {
        this.$formSection.empty();

        const roomGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.room") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(this.$formSection)
            .find("> .col-xl-6");
        this.roomSelect = new Select<IRoom>(
            undefined,
            RequestCache.roomDataSource(),
            (c) => c.name,
            false,
            i18n("modal.tableBulk.selectRoom")
        ).renderTo(roomGroup);
        $('<span class="form-text text-muted">' + i18n("modal.tableBulk.createAtLeast") + "</span>").insertAfter(
            roomGroup.find(".dropdown")
        );

        this.$formSection.append(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.tableBulk.tableNrFromTo") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3"><input type="text" name="createfrom" class="form-control" placeholder="' +
                i18n("modal.tableBulk.tableNrPlaceholder") +
                '" /></div><div class="col-xl-3"><input type="text" name="createto" class="form-control" placeholder="' +
                i18n("modal.tableBulk.tableNrPlaceholder") +
                '" /></div></div>'
        );
        this.$createFromField = this.$formSection.find('input[name="createfrom"]')["TouchSpin"]({
            min: 1,
            max: 100000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.$createToField = this.$formSection.find('input[name="createto"]')["TouchSpin"]({
            min: 1,
            max: 100000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.$createFromField!.change(() => {
            const f = this.$createFromField!.val().length === 0 ? undefined : parseInt(this.$createFromField!.val());
            const t = this.$createToField!.val().length === 0 ? undefined : parseInt(this.$createToField!.val());

            if ((f !== undefined && f > t!) || t === undefined) this.$createToField!.val(f!);

            this.fetchPreview();
        });

        this.$createToField!.change(() => {
            const f = this.$createFromField!.val().length === 0 ? undefined : parseInt(this.$createFromField!.val());
            const t = this.$createToField!.val().length === 0 ? undefined : parseInt(this.$createToField!.val());

            if ((t !== undefined && t < f!) || f === undefined) this.$createFromField!.val(t!);

            this.fetchPreview();
        });

        const editPosIdBtn = $(
            '<a href="#" class="editposid"><i class="la la-edit"></i> ' + i18n("table.editPosId") + "</a>"
        ).appendTo(this.$createFromField!.parent().parent());
        const posIdRow = $(
            '<div class="form-group row" id="posidrow"><label class="col-xl-4 col-form-label">' +
                i18n("modal.tableBulk.posId") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6"><input type="text" name="posconvention" class="form-control" placeholder="z.B. posid[Number]" value="t[Number]" /></div></div>'
        ).appendTo(this.$formSection);

        const tableNameRow = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.fields.designation") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(this.$formSection)
            .find("> .col-xl-6");
        const tableNameGroup = $(
            '<div class="input-group"><input type="text" name="nameconvention" class="form-control" placeholder="' +
                i18n("modal.tableBulk.conventionPlaceholder") +
                '" /></div>'
        ).appendTo(tableNameRow);
        tableNameRow.append('<span class="form-text text-muted">' + i18n("modal.tableBulk.conventionText") + "</span>");
        this.$nameConvField = tableNameGroup.find('input[name="nameconvention"]');
        $(
            '<div class="input-group-append"><button class="btn btn-secondary" type="button">' +
                i18n("modal.tableBulk.insertNumber") +
                "</button></div>"
        )
            .appendTo(tableNameGroup)
            .find("> .btn")
            .click((e) => {
                e.preventDefault();

                const existingVal = this.$nameConvField!.val();
                if (!existingVal.includes("[Number]")) {
                    let appendable = "[Number]";
                    if (
                        existingVal.length !== 0 &&
                        existingVal.substring(existingVal.length - 1, existingVal.length) !== " "
                    )
                        appendable = " " + appendable;

                    this.$nameConvField!.val(this.$nameConvField!.val() + appendable);
                }
            });

        const seatsRow = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.tableBulk.seatCount") +
                ' <span class="mandatory">*</span></label><div class="col-xl-5"></div><div class="col-xl-1"></div></div>'
        ).appendTo(this.$formSection);

        this.$seatsField = $('<input type="text" name="seats" class="form-control" />').appendTo(
            seatsRow.find("> .col-xl-1")
        );
        this.seatSlider = noUiSlider.create(
            $('<div class="kt-nouislider--drag-success" style="margin-top:11px;"></div>').appendTo(
                seatsRow.find("> .col-xl-5")
            )[0],
            {
                start: [2],
                step: 1,
                format: {
                    to: (value) => Math.trunc(value) + "",
                    from: (value) => parseInt(value),
                },
                range: {
                    min: [0],
                    max: [16],
                },
            }
        );

        this.$preferredMinimumOccupancyRow = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
            i18n("modal.tableBulk.preferredMinimumOccupancy") +
            ' <span class="mandatory">*</span></label><div class="col-xl-5"></div><div class="col-xl-1"></div></div>'
        ).appendTo(this.$formSection);

        this.$preferredMinimumOccupancyField = $('<input type="text" name="preferredMinimumOccupancy" class="form-control" />').appendTo(
            this.$preferredMinimumOccupancyRow.find("> .col-xl-1")
        );
        
        this.preferredMinimumOccupancySlider = noUiSlider.create(
            $('<div class="kt-nouislider--drag-success" style="margin-top:11px;"></div>').appendTo(
                this.$preferredMinimumOccupancyRow.find("> .col-xl-5")
            )[0],
            {
                start: [1],
                step: 1,
                format: {
                    to: (value) => Math.trunc(value) + "",
                    from: (value) => parseInt(value),
                },
                range: {
                    min: [1],
                    max: [16],
                },
            }
        );

        this.$preferredMinimumOccupancyField.change(() => {
            this.preferredMinimumOccupancySlider.set(this.$preferredMinimumOccupancyField!.val());
        });

        this.preferredMinimumOccupancySlider.on("update", (values, handle) => {
            this.$preferredMinimumOccupancyField!.val(values[handle]);
        });


        this.seatSlider.on("update", (values, handle) => {
            this.$seatsField!.val(values[handle]);

            if (this.$seatsField!.val() > 0) {
                this.$preferredMinimumOccupancyRow?.show();
            } else {
                this.$preferredMinimumOccupancyRow?.hide();
            }
            
            this.preferredMinimumOccupancySlider.updateOptions({
                range: {
                    min: [1],
                    max: [parseInt(values[handle])],
                },
            }, true)

        });

        this.$seatsField.change(() => {
            this.seatSlider.set(this.$seatsField!.val());
        });

        const togglePosIdVisiblity = () => {
            if (posIdRow.hasClass("shown")) {
                editPosIdBtn.html('<i class="la la-edit"></i> ' + i18n("table.editPosId"));
                posIdRow.removeClass("shown");
            } else {
                editPosIdBtn.html('<i class="la la-floppy-o"></i> ' + i18n("table.hidePosId"));
                posIdRow.addClass("shown");
            }
        };

        editPosIdBtn.click((e) => {
            e.preventDefault();

            togglePosIdVisiblity();
        });

        this.$posIdConvField = this.$formSection.find('input[name="posconvention"]');
        this.$form.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();

            return false;
        });
    }

    private fetchPreview() {
        this.$preview.empty();

        if (this.previewTimeout !== undefined) clearTimeout(this.previewTimeout);

        // if (this.previewPromise !== undefined) this.previewPromise!.cancel();

        this.previewTimeout = window.setTimeout(() => {
            const cmd = this.getCommandDto(false);

            //disable buttons
            if (!this.$submitBtn.hasClass("disabled")) this.$submitBtn.addClass("disabled").prop("disabled", true);

            if (
                (cmd.createFrom !== undefined && cmd.createTo !== undefined) ||
                (cmd.removeFrom !== undefined && cmd.removeTo !== undefined)
            ) {
                this.$preview.html(
                    '<div class="kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--success disabled" />'
                );

                this.previewPromise = Factory.getInstance<TableDao>(TableDao)
                    .bulkPreview(cmd)
                    .then((r) => {
                        if (r.createsTables !== 0 || r.updatesTables !== 0 || r.deletesTables !== 0)
                            this.$submitBtn.removeClass("disabled").prop("disabled", false);

                        if (cmd.removeFrom !== undefined || cmd.removeTo !== undefined) {
                            this.$preview.html(
                                '<i class="fa fa-exclamation-triangle"></i>' +
                                    (r.deletesTables === 0
                                        ? i18n("modal.tableBulk.previewNoneDelete")
                                        : r.deletesTables === 1
                                          ? i18n("modal.tableBulk.previewSingularDelete")
                                          : i18n("modal.tableBulk.previewPluralDelete", {
                                                amount: r.deletesTables,
                                            }))
                            );
                        } else {
                            if (r.updatesTables != 0)
                                this.$preview.html(
                                    '<i class="fa fa-exclamation-triangle"></i>' +
                                        (r.updatesTables === 1
                                            ? i18n("modal.tableBulk.previewSingularEdit")
                                            : i18n("modal.tableBulk.previewPluralEdit", {
                                                  amount: r.updatesTables,
                                              }))
                                );
                            else this.$preview.empty();
                        }

                        this.previewPromise = undefined;
                        this.previewTimeout = undefined;
                    });
            }
        }, 500);
    }
}
